<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Search, CornerDownLeft, Sun, Moon, LogOut } from '@lucide/vue';
import { NAV_GROUPS } from '@/navigation';
import { useAuthStore } from '@/stores/auth';
import { useThemeStore } from '@/stores/theme';

const open = defineModel<boolean>('open', { default: false });
const emit = defineEmits<{ (e: 'logout'): void }>();

const router = useRouter();
const auth = useAuthStore();
const theme = useThemeStore();

const query = ref('');
const active = ref(0);
const input = ref<HTMLInputElement | null>(null);

interface Item { id: string; label: string; group: string; icon: any; run: () => void; hay: string }

const items = computed<Item[]>(() => {
  const out: Item[] = [];
  for (const g of NAV_GROUPS) {
    for (const l of g.links) {
      if (!auth.can(l.perm)) continue;
      out.push({
        id: l.to, label: l.label, group: g.label, icon: l.icon,
        run: () => router.push(l.to),
        hay: `${l.label} ${g.label} ${l.keywords ?? ''}`.toLowerCase(),
      });
    }
  }
  out.push({
    id: 'theme', label: theme.effective === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
    group: 'Actions', icon: theme.effective === 'dark' ? Sun : Moon, run: () => theme.toggle(), hay: 'theme dark light mode',
  });
  out.push({ id: 'logout', label: 'Sign out', group: 'Actions', icon: LogOut, run: () => emit('logout'), hay: 'logout sign out exit' });
  return out;
});

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return items.value;
  const words = q.split(/\s+/);
  return items.value
    .filter((i) => words.every((w) => i.hay.includes(w)))
    .sort((a, b) => Number(!a.label.toLowerCase().startsWith(q)) - Number(!b.label.toLowerCase().startsWith(q)));
});

watch(query, () => { active.value = 0; });
watch(open, async (v) => {
  if (v) {
    query.value = '';
    active.value = 0;
    await nextTick();
    input.value?.focus();
  }
});

function run(i: Item | undefined) {
  if (!i) return;
  open.value = false;
  i.run();
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'ArrowDown') { e.preventDefault(); active.value = Math.min(active.value + 1, filtered.value.length - 1); scrollActive(); }
  else if (e.key === 'ArrowUp') { e.preventDefault(); active.value = Math.max(active.value - 1, 0); scrollActive(); }
  else if (e.key === 'Enter') { e.preventDefault(); run(filtered.value[active.value]); }
  else if (e.key === 'Escape') { open.value = false; }
}

function scrollActive() {
  nextTick(() => document.querySelector('.cp-item.active')?.scrollIntoView({ block: 'nearest' }));
}

function onGlobalKey(e: KeyboardEvent) {
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    open.value = !open.value;
  }
}
onMounted(() => window.addEventListener('keydown', onGlobalKey));
onUnmounted(() => window.removeEventListener('keydown', onGlobalKey));
</script>

<template>
  <Teleport to="body">
    <Transition name="cp">
      <div v-if="open" class="cp-overlay" @mousedown.self="open = false">
        <div class="cp" role="dialog" aria-label="Command palette">
          <div class="cp-search">
            <Search />
            <input
              ref="input"
              v-model="query"
              placeholder="Jump to a section or run an action…"
              @keydown="onKeydown"
            />
            <kbd>Esc</kbd>
          </div>
          <div class="cp-list">
            <template v-for="(i, idx) in filtered" :key="i.id">
              <div v-if="idx === 0 || filtered[idx - 1].group !== i.group" class="cp-group">{{ i.group }}</div>
              <button
                class="cp-item"
                :class="{ active: idx === active }"
                @mouseenter="active = idx"
                @click="run(i)"
              >
                <component :is="i.icon" class="cp-ic" />
                <span>{{ i.label }}</span>
                <CornerDownLeft v-if="idx === active" class="cp-enter" />
              </button>
            </template>
            <div v-if="!filtered.length" class="cp-empty">Nothing found for “{{ query }}”</div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.cp-overlay {
  position: fixed;
  inset: 0;
  z-index: 250;
  background: var(--c-overlay);
  backdrop-filter: blur(3px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 12vh 16px 16px;
}
.cp {
  width: 100%;
  max-width: 560px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 70vh;
}
.cp-search {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  height: 52px;
  border-bottom: 1px solid var(--c-border);
}
.cp-search > svg { width: 18px; height: 18px; color: var(--c-text-3); flex-shrink: 0; }
.cp-search input {
  flex: 1;
  min-width: 0;
  background: none;
  border: none;
  outline: none;
  color: var(--c-text);
  font: inherit;
  font-size: 15px;
}
.cp-list { overflow-y: auto; padding: 6px; }
.cp-group {
  padding: 10px 10px 4px;
  font-size: var(--fs-xs);
  font-weight: 600;
  color: var(--c-text-3);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.cp-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border: none;
  background: none;
  color: var(--c-text);
  font: inherit;
  font-size: var(--fs-md);
  border-radius: var(--radius);
  cursor: pointer;
  text-align: left;
}
.cp-item.active { background: var(--c-primary-soft); }
.cp-ic { width: 16px; height: 16px; color: var(--c-text-2); }
.cp-item.active .cp-ic { color: var(--c-primary-text); }
.cp-enter { width: 14px; height: 14px; margin-left: auto; color: var(--c-text-3); }
.cp-empty { padding: 28px; text-align: center; color: var(--c-text-3); font-size: var(--fs-sm); }

.cp-enter-active, .cp-leave-active { transition: opacity 0.15s var(--ease); }
.cp-enter-active .cp, .cp-leave-active .cp { transition: transform 0.18s var(--ease); }
.cp-enter-from, .cp-leave-to { opacity: 0; }
.cp-enter-from .cp, .cp-leave-to .cp { transform: translateY(-8px) scale(0.98); }

@media (max-width: 640px) {
  .cp-overlay { padding: 12px; }
  .cp { max-height: 80dvh; }
  .cp-search input { font-size: 16px; }
}
</style>
